import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import type { MeDto, Capability } from "@arcline/shared";
import { api, token } from "./api";
import { Ctx, useAuth } from "./auth-context";
import { Loading } from "../components/DataState";

// Re-exported so existing `import { useAuth } from "../lib/auth"` call sites
// keep working after the context moved into auth-context.ts.
export { useAuth };

// Build-time auth mode (must match the server's AUTH_MODE). Defaults to the
// dev-shim so local dev / the test build need no Clerk config.
export const AUTH_MODE: "local" | "clerk" =
  import.meta.env.VITE_AUTH_MODE === "clerk" ? "clerk" : "local";

// The Clerk provider — and the ~180 KB Clerk SDK it imports — is code-split into
// its own async chunk and only fetched when running in clerk mode. The default
// local (dev-shim) build never downloads it, so first paint stays light.
const ClerkAuthRoot = lazy(() => import("./auth-clerk"));

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE === "clerk") {
    return (
      <Suspense fallback={<Loading label="Loading" minHeight={400} />}>
        <ClerkAuthRoot>{children}</ClerkAuthRoot>
      </Suspense>
    );
  }
  return <LocalAuthProvider>{children}</LocalAuthProvider>;
}

// ── LOCAL mode (dev-shim) ────────────────────────────────────────────────────
// email/password → our JWT, held in localStorage. Behavior unchanged.
function LocalAuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token.get()) { setLoading(false); return; }
    api.me().then(setMe).catch(() => token.clear()).finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    const { token: t, user } = await api.login(email, password);
    token.set(t);
    setMe(user);
  };
  const logout = (): void => { token.clear(); setMe(null); };
  const can = (cap: Capability): boolean =>
    me ? me.caps[cap] : false;

  return <Ctx.Provider value={{ me, loading, login, logout, can }}>{children}</Ctx.Provider>;
}
